<template>
	<div class="profile" :class="{ 'touch-device': isTouchDevice }" v-if="profileUid">
		<div class="user-profile" :class="{ selected: isSelected == true }" @click="isTouchDevice ? selectUser() : null">
			<div class="img-container">
				<img
					v-if="profile?.field_profile_photo?.field_media_image?.uri?.url"
					:src="url + profile?.field_profile_photo?.field_media_image?.uri?.url"
					alt=""
				/>
				<div v-else class="profile-avatar-placeholder">
					{{ profile?.field_name?.split(" ")[0].charAt(0) }}{{ profile?.field_name?.split(" ")[1]?.charAt(0) }}
				</div>
				<!-- Hover overlay for non-touch devices -->
				<div v-if="!isTouchDevice" class="user-profile--ctas">
					<button class="select-profile" @click="selectUser" v-tooltip.bottom="$t('profile.select')">
						<IconUserAccount />
					</button>
					<button class="edit-profile" @click="editProfile" v-tooltip.bottom="$t('profile.edit')">
						<IconEdit />
					</button>
				</div>
			</div>

			<div class="profile-name--info">
				<h2 class="user-profile vl-font-size--18 color-white">{{ profile?.field_name }}</h2>

				<!-- Touch device buttons - always visible, outside the image -->
				<div v-if="isTouchDevice && isSelected" class="touch-device-ctas">
					<button class="touch-edit-btn" @click.stop="editProfile">
						<IconEdit />
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import IconUserAccount from "../icons/IconUserAccount.vue"
	import { ref, computed } from "vue"
	import { useMediaQuery } from "@vueuse/core"
	import api from "../composables/useApi"
	import { url } from "../helper"
	import { useUserStore } from "../stores/user"
	import router from "../router/router"
	import { useMovieStore } from "../stores/movies"
	import { useSeriesStore } from "../stores/series"
	import IconEdit from "../icons/IconEdit.vue"

	const userStore = useUserStore()
	const movieStore = useMovieStore()
	const seriesStore = useSeriesStore()

	const props = defineProps<{
		profileUid: string
	}>()

	const profile = ref<any>(null)

	// Use VueUse to detect touch devices via media query
	const isTouchDevice = useMediaQuery("(pointer: coarse)")

	const getProfile = async () => {
		const response = await api.get(
			`/jsonapi/profile/subscriber_profile/${props.profileUid}?jsonapi_include=1&include=field_profile_photo,field_profile_photo.field_media_image`
		)
		profile.value = response.data.data
	}

	if (props.profileUid) {
		getProfile()
	}

	const editProfile = () => {
		userStore.state.edit_profile = profile.value
		router.push({ name: "AddUserProfile" })
	}

	const isSelected = computed(() => {
		if (localStorage.getItem("user_profile")) {
			const storedProfile = localStorage.getItem("user_profile")
			return storedProfile === props.profileUid
		}
		return userStore.state.selected_profile?.id === props.profileUid
	})

	const selectUser = () => {
		userStore.state.selected_profile = profile.value
		movieStore.state.movies = []
		seriesStore.state.series = []
		localStorage.setItem("user_profile", profile.value?.id)
		router.push("/home")
	}
</script>

<style lang="scss" scoped>
	.profile {
		position: relative;
		z-index: 0;

		&:hover:not(.touch-device) {
			.img-container {
				&::after {
					content: "";
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background-color: var(--red);
					border-radius: 50%;
					transition: background-color 0.3s ease;
					cursor: pointer;
				}

				.user-profile--ctas,
				.select-profile,
				.edit-profile {
					opacity: 1;
				}
			}
		}
	}

	.img-container {
		position: relative;
	}

	.select-profile,
	.edit-profile {
		font-size: 32px;
		color: var(--white);
		padding: 5px;
		z-index: 2;
		transition: all 0.3s ease;

		svg {
			width: 35px;
			height: 35px;
		}

		&:hover {
			background: var(--red);
			border-color: var(--white);
		}
	}

	.user-profile {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;

		&--ctas {
			opacity: 0;
			display: flex;
			align-items: center;
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			justify-content: center;
			gap: 4px;
		}

		&.selected {
			.profile-avatar-placeholder,
			img {
				border: 2px solid var(--red);
				padding: 10px;
			}
		}

		&:hover {
			cursor: pointer;
		}
	}

	// Touch device specific styles
	.touch-device-ctas {
		display: flex;
		gap: 12px;
		justify-content: center;

		button {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 10px 16px;
			border-radius: 8px;
			font-size: 14px;
			font-weight: 600;
			transition: all 0.3s ease;
			border: 1px solid rgba(255, 255, 255, 0.2);
			background: rgba(255, 255, 255, 0.05);
			color: var(--white);

			svg {
				width: 20px;
				height: 20px;
			}

			&:hover {
				background: rgba(255, 255, 255, 0.1);
				border-color: rgba(255, 255, 255, 0.3);
			}

			&:active {
				transform: scale(0.95);
			}
		}

		.touch-select-btn {
			&:hover {
				background: rgba(0, 150, 255, 0.2);
				border-color: rgba(0, 150, 255, 0.5);
			}
		}

		.touch-edit-btn {
			border-radius: 50%;
			width: 32px;
			height: 32px;
			padding: 0;
			text-align: center;
			display: flex;
			justify-content: center;
			&:hover {
				background: rgba(255, 193, 7, 0.2);
				border-color: rgba(255, 193, 7, 0.5);
			}
		}
	}

	img {
		width: 150px;
		height: 150px;
		display: block;
		border-radius: 50%;
		object-fit: cover;
		object-position: center;
		background: var(--gray);
	}

	.profile-avatar-placeholder {
		width: 150px;
		height: 150px;
		border-radius: 50%;
		background: var(--dark-gray);
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 48px;
		color: white;
	}

	.profile-name--info {
		display: flex;
		align-items: center;
		gap: 8px;
	}
</style>
